import { Controller } from "@hotwired/stimulus"

export default class SliderController extends Controller {
  static targets = ["slider", "prev", "next"]

  connect() {
    this.isDown = false
    this.isDragging = false

    this.updateButtons = this.updateButtons.bind(this)
    this.mouseUp = this.mouseUp.bind(this)
    this.mouseMove = this.mouseMove.bind(this)

    this.sliderTarget.addEventListener("mousedown", this.mouseDown.bind(this))
    this.sliderTarget.addEventListener("mousemove", this.mouseMove)
    document.addEventListener("mouseup", this.mouseUp)
    globalThis.addEventListener("resize", this.updateButtons)

    if ("onscrollend" in window) {
      this.sliderTarget.addEventListener("scrollend", this.updateButtons)
    } else {
      this.debouncedUpdateButtons = this.debounce(this.updateButtons, 120)
      this.sliderTarget.addEventListener("scroll", this.debouncedUpdateButtons)
    }

    this.resizeObserver = new ResizeObserver(this.updateButtons)
    this.resizeObserver.observe(this.sliderTarget)

    this.updateButtons()
  }

  disconnect() {
    document.removeEventListener("mouseup", this.mouseUp)
    globalThis.removeEventListener("resize", this.updateButtons)
    this.sliderTarget.removeEventListener("scrollend", this.updateButtons)
    if (this.debouncedUpdateButtons) {
      this.sliderTarget.removeEventListener("scroll", this.debouncedUpdateButtons)
    }
    this.resizeObserver?.disconnect()
    clearTimeout(this.debounceTimeout)
  }

  debounce(fn, wait) {
    return (...args) => {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  get slides() {
    return Array.from(this.sliderTarget.children)
  }

  getStep() {
    const slides = this.slides
    if (slides.length < 2) return this.sliderTarget.clientWidth

    const r0 = slides[0].getBoundingClientRect()
    const r1 = slides[1].getBoundingClientRect()
    return r1.left - r0.left
  }

  currentIndex(scrollLeft = this.sliderTarget.scrollLeft) {
    const step = this.getStep()
    if (!step) return 0

    const maxIndex = this.slides.length - 1
    return Math.min(maxIndex, Math.max(0, Math.round(scrollLeft / step)))
  }

  targetScrollLeft(index) {
    const slides = this.slides
    const maxIndex = slides.length - 1
    const maxScrollLeft = this.sliderTarget.scrollWidth - this.sliderTarget.clientWidth

    if (index <= 0) return 0
    if (index >= maxIndex) return maxScrollLeft

    const rect0 = slides[0].getBoundingClientRect()
    const rectI = slides[index].getBoundingClientRect()
    const measured = rectI.left - rect0.left

    return Math.min(measured, maxScrollLeft)
  }

  goToSlide(index) {
    const maxIndex = this.slides.length - 1
    const clamped = Math.min(maxIndex, Math.max(0, index))
    const target = this.targetScrollLeft(clamped)

    this.sliderTarget.scrollTo({ left: target, behavior: "smooth" })
    this.setButtonState(target)

    return clamped
  }
  prev(event) {
    event.preventDefault()
    this.goToSlide(this.currentIndex() - 1)
  }

  next(event) {
    event.preventDefault()
    this.goToSlide(this.currentIndex() + 1)
    event.currentTarget.blur()
  }

  mouseDown(event) {
    this.isDown = true
    this.isDragging = false
    this.sliderTarget.classList.add("dragging")
    this.startX = event.pageX
    this.scrollLeft = this.sliderTarget.scrollLeft
  }

  mouseUp(event) {
    if (!this.isDown) return

    this.isDown = false
    this.sliderTarget.classList.remove("dragging")

    const dragged = this.isDragging
    const walk = dragged ? event.pageX - this.startX : 0
    const baseIndex = this.currentIndex(this.scrollLeft)

    let targetIndex = baseIndex
    if (walk > 5) {
      targetIndex = baseIndex - 1
    } else if (walk < -5) {
      targetIndex = baseIndex + 1
    }

    this.goToSlide(targetIndex)

    setTimeout(() => {
      this.isDragging = false
    }, 10)
  }

  mouseMove(event) {
    if (!this.isDown) return

    event.preventDefault()

    const walk = event.pageX - this.startX

    if (Math.abs(walk) > 5) this.isDragging = true

    this.sliderTarget.scrollLeft = this.scrollLeft - walk
  }

  updateButtons() {
    this.setButtonState(this.sliderTarget.scrollLeft)
  }

  setButtonState(scrollLeft) {
    const hasMultipleRacks = this.slides.length > 1
    const hasOverflow =
      this.sliderTarget.scrollWidth > this.sliderTarget.clientWidth + 1
    const showButtons = hasMultipleRacks && hasOverflow

    const atStart = scrollLeft <= 1
    const atEnd =
      Math.ceil(scrollLeft + this.sliderTarget.clientWidth) >=
      this.sliderTarget.scrollWidth - 1

    if (this.hasPrevTarget) {
      this.prevTarget.classList.toggle("d-none", !showButtons || atStart)
      this.prevTarget.disabled = !showButtons || atStart
    }

    if (this.hasNextTarget) {
      this.nextTarget.classList.toggle("d-none", !showButtons || atEnd)
      this.nextTarget.disabled = !showButtons || atEnd
    }
  }
}